/**
 * MultiRefundDisplay.jsx
 *
 * Shows every refund event linked to a bill — covers the case where a single
 * bill has BOTH a primary refund (bill.refund_to=Anchor) AND one or more
 * supplementary Customer-variance refunds recorded in pm_refund_events.
 *
 * Renders nothing if there are no events. Always fetches the latest list
 * from /api/payments/v2/bill-refund-events/{bill_id}.
 *
 * Props:
 *   billId       (string)  the bill_id
 *   accountId    (string)  the account_id
 *   primaryRefund (object|null) optional: bill-level refund summary to display
 *                  alongside the events list — { refund_amount, refund_to,
 *                  refund_completed, refund_date }
 *   onEditPrimary    (() => void)  optional callback to edit the bill-level refund
 *   onDeletePrimary  (() => Promise|void) optional callback to clear/delete the bill-level refund
 *   onEditEvent      ((event) => void) optional callback to edit a pm_refund_events row
 *   onDeleteEvent    ((event) => Promise) optional callback to delete an event
 *   refreshKey   (any)     pass a changing value to force re-fetch
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Loader2, ArrowRightLeft, Building2, Users, ShieldCheck, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

const fmtC = (v) =>
  v == null ? '—' : '$' + Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const fmtD = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString(); } catch { return v; }
};

const refundToIcon = (to) => {
  const t = (to || '').toLowerCase();
  if (t === 'anchor') return <Building2 className="h-3 w-3 text-blue-500" />;
  if (t === 'customer') return <Users className="h-3 w-3 text-emerald-500" />;
  if (t === 'billportal') return <ShieldCheck className="h-3 w-3 text-purple-500" />;
  return <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />;
};

export default function MultiRefundDisplay({
  billId,
  accountId,
  primaryRefund = null,
  onEditPrimary,
  onDeletePrimary,
  onEditEvent,
  onDeleteEvent,
  refreshKey,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingPrimary, setDeletingPrimary] = useState(false);

  const reload = () => {
    if (!billId || !accountId) return;
    setLoading(true);
    return api.get(`/payments/v2/bill-refund-events/${billId}`, { params: { account_id: accountId } })
      .then(({ data }) => setEvents(data?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!billId || !accountId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/payments/v2/bill-refund-events/${billId}`, { params: { account_id: accountId } })
      .then(({ data }) => { if (!cancelled) setEvents(data?.events || []); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [billId, accountId, refreshKey]);

  const handleDelete = async (ev) => {
    if (!onDeleteEvent) return;
    setDeletingId(ev.id);
    try {
      await onDeleteEvent(ev);
      await reload();
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeletePrimary = async () => {
    if (!onDeletePrimary) return;
    setDeletingPrimary(true);
    try {
      await onDeletePrimary();
      await reload();
    } finally {
      setDeletingPrimary(false);
    }
  };

  const hasPrimary = primaryRefund && Number(primaryRefund.refund_amount || 0) > 0.01;
  if (!hasPrimary && events.length === 0 && !loading) return null;

  return (
    <div
      className="border border-red-200 rounded-md bg-red-50/30 p-3 space-y-2"
      data-testid="multi-refund-display"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-red-700">
          Refund History
        </p>
        {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>

      {/* Primary refund (the bill-level refund_to / refund_amount) */}
      {hasPrimary && (
        <div className="text-xs flex items-center justify-between rounded bg-background border border-red-100 px-2.5 py-1.5 gap-2"
          data-testid="multi-refund-primary">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {refundToIcon(primaryRefund.refund_to)}
            <span className="font-mono font-semibold text-red-700">
              {fmtC(primaryRefund.refund_amount)}
            </span>
            <span className="text-muted-foreground truncate">
              to <strong className="text-foreground">{primaryRefund.refund_to || '—'}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] text-muted-foreground">{fmtD(primaryRefund.refund_date)}</span>
            <span className={
              `inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                primaryRefund.refund_completed
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-amber-100 text-amber-700'
              }`
            }>
              {primaryRefund.refund_completed ? 'Refunded' : 'To refund'}
            </span>
            {onEditPrimary && (
              <Button
                variant="ghost" size="sm" className="h-6 w-6 p-0"
                onClick={onEditPrimary}
                title="Edit primary refund"
                data-testid="multi-refund-edit-primary"
              >
                <Pencil className="h-3 w-3" />
              </Button>
            )}
            {onDeletePrimary && (
              <Button
                variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                onClick={handleDeletePrimary}
                disabled={deletingPrimary}
                title="Delete primary refund"
                data-testid="multi-refund-delete-primary"
              >
                {deletingPrimary
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <Trash2 className="h-3 w-3" />}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Additional refund events from pm_refund_events */}
      {events.map((ev) => (
        <div
          key={ev.id}
          className="text-xs flex items-center justify-between rounded bg-background border border-red-100 px-2.5 py-1.5 gap-2"
          data-testid={`multi-refund-event-${ev.id}`}
        >
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {refundToIcon(ev.refund_to)}
            <span className="font-mono font-semibold text-red-700">{fmtC(ev.refund_amount)}</span>
            <span className="text-muted-foreground truncate">
              to <strong className="text-foreground">{ev.refund_to || '—'}</strong>
            </span>
            {ev.transaction_reference && (
              <span className="text-[10px] text-muted-foreground truncate" title={ev.transaction_reference}>
                · ref {ev.transaction_reference}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] text-muted-foreground">{fmtD(ev.refund_date)}</span>
            <span className={
              `inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                ev.status === 'completed'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-amber-100 text-amber-700'
              }`
            }>
              {ev.status === 'completed' ? 'Refunded' : 'To refund'}
            </span>
            {onEditEvent && (
              <Button
                variant="ghost" size="sm" className="h-6 w-6 p-0"
                onClick={() => onEditEvent(ev)}
                title="Edit refund"
                data-testid={`multi-refund-edit-${ev.id}`}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            )}
            {onDeleteEvent && (
              <Button
                variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                onClick={() => handleDelete(ev)}
                disabled={deletingId === ev.id}
                title="Delete refund"
                data-testid={`multi-refund-delete-${ev.id}`}
              >
                {deletingId === ev.id
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <Trash2 className="h-3 w-3" />}
              </Button>
            )}
          </div>
        </div>
      ))}

      {(events.length > 0 || hasPrimary) && (
        <p className="text-[10px] text-muted-foreground pt-1 border-t border-red-100">
          {events.length + (hasPrimary ? 1 : 0)} refund entr{(events.length + (hasPrimary ? 1 : 0)) === 1 ? 'y' : 'ies'} on this bill.
        </p>
      )}
    </div>
  );
}
